import { Search, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { getDevice } from "@/utils/device";
import { useHeaderFlags } from "../useHeaderFlags";
import { EnquirySearchLightbox } from "./EnquirySearchLightbox";

export const GlobalSearch: React.FC = () => {
  const { forceIpad } = useHeaderFlags();
  // getDevice() returns "mobile" for iPad/iPhone user agents and for any
  // window with ReactNativeWebView injected — reliable across the webview
  // whether the native wrapper injects the global or not. forceIpad lets us
  // preview the native branch in a browser.
  const isNative = getDevice() === "mobile" || forceIpad;

  if (isNative) {
    return <NativeSearch />;
  }
  return <InlineSearch />;
};

const NativeSearch: React.FC = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open search"
        className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent"
      >
        <Search size={18} aria-hidden="true" />
      </button>
      <EnquirySearchLightbox open={open} onOpenChange={setOpen} />
    </>
  );
};

const InlineSearch: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(() => searchParams.get("query") ?? "");
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Keep the input in sync with the URL query param so the search term
  // persists after navigation (e.g. redirecting to /enquiries?query=...).
  useEffect(() => {
    setQuery(searchParams.get("query") ?? "");
  }, [searchParams]);

  // Auto-focus the input when the compact dropdown opens.
  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  // Close the dropdown when clicking outside.
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent): void => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleSubmit = (): void => {
    const trimmed = query.trim();
    if (!trimmed) return;
    setIsOpen(false);
    navigate(`/enquiries?query=${encodeURIComponent(trimmed)}`);
  };

  const handleClear = (): void => {
    setQuery("");
    setIsOpen(false);
    navigate("/enquiries");
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ): void => {
    if (event.key === "Enter") {
      handleSubmit();
    }
    if (event.key === "Escape") {
      setIsOpen(false);
    }
  };

  const hasQuery = query.length > 0;

  return (
    <div
      ref={wrapperRef}
      className="relative flex items-center md:flex-1 md:min-w-0 md:max-w-xs"
    >
      {/* Compact toggle — visible below md */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={isOpen ? "Close search" : "Open search"}
        aria-expanded={isOpen}
        className="md:hidden p-1.5 rounded-md text-muted-foreground hover:text-foreground"
      >
        {isOpen ? (
          <X size={16} aria-hidden="true" />
        ) : (
          <Search size={16} aria-hidden="true" />
        )}
      </button>

      {/* Search panel — dropdown on narrow when open, inline on md+ */}
      <div
        className={`${
          isOpen
            ? "absolute left-0 top-full mt-1 z-50 w-72 p-2 rounded-md border border-border bg-[var(--pageBackground)] shadow-lg"
            : "hidden"
        } md:relative md:left-auto md:top-auto md:block md:w-full md:p-0 md:mt-0 md:border-0 md:bg-transparent md:shadow-none md:rounded-none md:z-auto`}
      >
        <div className="relative">
          <input
            ref={inputRef}
            type="search"
            name="global-search-query"
            placeholder="Search bookings by ID, name or email"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            maxLength={200}
            aria-label="Global search"
            autoComplete="none"
            className="w-full pl-3 pr-14 py-1.5 text-sm rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {hasQuery && (
            <button
              type="button"
              onClick={handleClear}
              aria-label="Clear search"
              className="absolute right-8 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X size={16} aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            aria-label="Submit search"
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <Search size={16} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
};
